import { isUploadImageOverLimitError } from './errors';

// Message attachments are served from Sharetribe storage with a
// `Content-Disposition: attachment` header, which forces browsers to download
// them. For file types the browser can render, the openAttachment thunk
// instead fetches the bytes and opens them as a blob URL. These helpers decide
// which types are "openable" and map an extension to a MIME type for the blob
// fallback when the storage response lacks a usable Content-Type.

// File extensions the browser reliably renders inline, mapped to their MIME type.
//
// SECURITY: SVG is deliberately excluded. Attachments are opened as a blob URL
// in a top-level tab, and that blob URL inherits our origin. An SVG (or HTML)
// document opened top-level executes any embedded <script> — as first-party
// script on our origin — which would be a stored-XSS vector via a malicious
// attachment. Only inert formats (PDF, raster images) belong here; SVGs keep
// the download path.
const INLINE_EXTENSIONS = {
  pdf: 'application/pdf',
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  webp: 'image/webp',
  avif: 'image/avif',
};

// The set of MIME types we are willing to render inline (the values above).
// Used as a second gate against Content-Type spoofing: a file with an openable
// extension whose actual served type is active content (e.g. text/html,
// image/svg+xml) must NOT be rendered inline.
const INLINE_RENDER_MIME_TYPES = new Set(Object.values(INLINE_EXTENSIONS));

// Lower-cased extension without the dot, or '' when there is none.
export const fileExtension = fileName => {
  if (!fileName || typeof fileName !== 'string') {
    return '';
  }
  const lastDot = fileName.lastIndexOf('.');
  if (lastDot < 0 || lastDot === fileName.length - 1) {
    return '';
  }
  return fileName.slice(lastDot + 1).toLowerCase();
};

// True when the browser can render this file inline (PDF or image).
export const isBrowserOpenable = fileName =>
  Object.prototype.hasOwnProperty.call(INLINE_EXTENSIONS, fileExtension(fileName));

// MIME type for an openable file's extension, or '' when unknown.
export const mimeTypeForFileName = fileName => INLINE_EXTENSIONS[fileExtension(fileName)] || '';

// True when a resolved MIME type is one we trust to render inline in a browser
// tab (inert formats only). Guards against Content-Type spoofing where an
// openable extension is served as active content.
export const isInlineRenderableMimeType = mimeType => INLINE_RENDER_MIME_TYPES.has(mimeType);

// Which preview tile a feed attachment gets: real thumbnail for raster
// images, a generic card for PDFs, none for everything else.
export const previewKind = fileName => {
  if (!isBrowserOpenable(fileName)) {
    return null;
  }
  return fileExtension(fileName) === 'pdf' ? 'pdf' : 'image';
};

// ---------------------------------------------------------------------------
// Client-side limits and error mapping for the attachment pickers.
// ---------------------------------------------------------------------------

// Sharetribe enforces these on the backend too: max 10 files per message, 1GB per file.
export const MAX_MESSAGE_ATTACHMENTS = 10;
export const MAX_ATTACHMENT_SIZE_BYTES = 1024 * 1024 * 1024;

// Split a file-picker selection into the files to upload and the reason the
// last rejected file was skipped (a translation id, or null when nothing was
// rejected). `stagedCount` is how many files the composer already holds.
export const partitionFilesForUpload = (files, stagedCount) => {
  let remaining = MAX_MESSAGE_ATTACHMENTS - stagedCount;
  let errorId = null;
  const accepted = [];
  files.forEach(file => {
    if (remaining <= 0) {
      errorId = 'MessageAttachmentsPicker.maxFilesReached';
    } else if (file.size > MAX_ATTACHMENT_SIZE_BYTES) {
      errorId = 'MessageAttachmentsPicker.fileTooLarge';
    } else {
      remaining -= 1;
      accepted.push(file);
    }
  });
  return { accepted, errorId };
};

// Map a failed upload to the most specific translated error we can tell apart.
export const failedUploadMessageId = error => {
  if (isUploadImageOverLimitError(error) || error?.status === 413) {
    return 'MessageAttachmentsPicker.fileTooLarge';
  }
  if (error?.status === 400) {
    return 'MessageAttachmentsPicker.unsupportedFileType';
  }
  return 'MessageAttachmentsPicker.uploadFailed';
};
